import React, { useState } from "react";
import { Button, Modal, message } from "antd";
import "../../../css/SettingsTab.css";
import { getWebhookSecret } from "../../../services/integrations/klaviyoSubscriptionService";
import WebhookBodyTemplates from "./WebhookBodyTemplates";

// Read by the running backend authenticator (see connectorSubscriptions.js /
// webhook-secret). A typo here sends every client who follows this card to a
// 401 with no explanation.
const HEADER_NAME = "X-BluStream-Webhook-Secret";

function handleCopy(text) {
  navigator.clipboard
    .writeText(text)
    .then(() => message.success("Copied"))
    .catch(() => message.error("Failed to copy"));
}

function ValueRow({ label, hint, children }) {
  return (
    <div className="settings-row">
      <div className="settings-field">
        <div className="settings-label">{label}</div>
        {hint && <div className="settings-hint">{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{children}</div>
    </div>
  );
}

export default function WebhookSetupCard({ webhookUrl, onRotate }) {
  // This card only mounts inside SettingsTab, which KlaviyoConfiguration only renders once a
  // subscription exists -- so a falsy webhookUrl here is never "not connected." It means the
  // summary fetch is still in flight, failed, or the backend returned null. Both
  // revealWebhookSecret and rotateWebhookSecret also 404 server-side without a subscription,
  // so Reveal/Rotate must not be live buttons a client can click into a guaranteed failure.
  const hasSubscription = Boolean(webhookUrl);
  const [secret, setSecret] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const res = await getWebhookSecret();
      setSecret(res?.webhook_secret);
    } catch {
      message.error("Failed to load webhook secret");
    }
    setRevealing(false);
  };

  const handleRotateConfirm = () => {
    setRotateModalOpen(false);
    // The old secret is now invalid everywhere -- drop any revealed copy so
    // the card doesn't keep showing a value that no longer authenticates.
    setSecret(null);
    onRotate();
  };

  return (
    <div className="settings-tab-container">
      <div className="settings-section">
        <div className="settings-section-title">Webhook Setup</div>

        <ValueRow
          label="Destination URL"
          hint={!webhookUrl && "Unable to load your webhook URL right now. Refresh the page, or try again shortly."}
        >
          {webhookUrl ? (
            <>
              <span style={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
                {webhookUrl}
              </span>
              <Button onClick={() => handleCopy(webhookUrl)}>Copy</Button>
            </>
          ) : (
            <span style={{ fontSize: 13, color: "#999" }}>Not available</span>
          )}
        </ValueRow>

        <ValueRow label="Header Name">
          <span style={{ fontFamily: "monospace", fontSize: 13 }}>{HEADER_NAME}</span>
          <Button onClick={() => handleCopy(HEADER_NAME)}>Copy</Button>
        </ValueRow>

        <ValueRow
          label="Header Value (Secret)"
          hint={
            hasSubscription
              ? "Hidden until you reveal it, to keep it out of logs and error reports."
              : null
          }
        >
          {!hasSubscription ? (
            <span style={{ fontSize: 13, color: "#999" }}>
              Unavailable right now. Refresh the page, or try again shortly.
            </span>
          ) : secret ? (
            <>
              <span style={{ fontFamily: "monospace", fontSize: 13 }}>{secret}</span>
              <Button onClick={() => handleCopy(secret)}>Copy</Button>
            </>
          ) : (
            <>
              <span style={{ fontFamily: "monospace", fontSize: 13, color: "#999" }}>
                ••••••••••••••••
              </span>
              <Button onClick={handleReveal} loading={revealing}>
                Reveal
              </Button>
            </>
          )}
        </ValueRow>

        {/* Deliberately separated from the Copy controls above so it isn't hit by accident. */}
        <div className="settings-row" style={{ justifyContent: "flex-end" }}>
          <Button
            danger
            disabled={!hasSubscription}
            title={hasSubscription ? undefined : "Unavailable until your webhook URL loads."}
            onClick={() => setRotateModalOpen(true)}
          >
            Rotate Secret
          </Button>
        </div>
      </div>

      <WebhookBodyTemplates />

      <Modal
        title="Rotate webhook secret?"
        open={rotateModalOpen}
        onCancel={() => setRotateModalOpen(false)}
        width={450}
        footer={[
          <Button key="cancel" onClick={() => setRotateModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="rotate" danger type="primary" onClick={handleRotateConfirm}>
            Rotate
          </Button>,
        ]}
      >
        <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          Rotating replaces the secret everywhere. Any flow still sending the old value stops
          working until you update it.
        </p>
      </Modal>
    </div>
  );
}
